/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-11-05T04:17:10.246Z
 * 소스 파일: src/data/dto/account-setup-identity-response-dto
 */

import type { AccountSetupIdentityResponseDto } from "@/data/dto/account-setup-identity-response-dto";

/**
 * AccountSetupIdentityResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isAccountSetupIdentityResponseDto(input: unknown): input is AccountSetupIdentityResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    typeof obj.hospitalName === "string" && obj.hospitalName.trim() !== "" &&
    typeof obj.guardianFullName === "string" && obj.guardianFullName.trim() !== "" &&
    typeof obj.patient === "object" && obj.patient !== null && !Array.isArray(obj.patient) &&
typeof (obj.patient as any).fullName === "string" && (obj.patient as any).fullName.trim() !== "" &&
typeof (obj.patient as any).birthDate === "string"
  );
}

/**
 * AccountSetupIdentityResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateAccountSetupIdentityResponseDto(input: unknown): IValidation<AccountSetupIdentityResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // hospitalName (required)
      if (objObj.hospitalName === undefined) {
        errors.push({ path: "hospitalName", expected: "string", value: undefined });
      } else {
        if (!(typeof objObj.hospitalName === "string" && objObj.hospitalName.trim() !== "")) {
          errors.push({ path: "hospitalName", expected: "string (non-empty)", value: objObj.hospitalName });
        }
      }
      // guardianFullName (required)
      if (objObj.guardianFullName === undefined) {
        errors.push({ path: "guardianFullName", expected: "string", value: undefined });
      } else {
        if (!(typeof objObj.guardianFullName === "string" && objObj.guardianFullName.trim() !== "")) {
          errors.push({ path: "guardianFullName", expected: "string (non-empty)", value: objObj.guardianFullName });
        }
      }
      // patient (required)
      if (objObj.patient === undefined) {
        errors.push({ path: "patient", expected: "object", value: undefined });
      } else {
        if (typeof objObj.patient !== "object" || objObj.patient === null || Array.isArray(objObj.patient)) {
          errors.push({ path: "patient", expected: "object", value: objObj.patient });
        } else {
          const objObj_patientObj = objObj.patient as Record<string, unknown>;
          // fullName (required)
          if (objObj_patientObj.fullName === undefined) {
            errors.push({ path: "patient.fullName", expected: "string", value: undefined });
          } else {
            if (!(typeof objObj_patientObj.fullName === "string" && objObj_patientObj.fullName.trim() !== "")) {
              errors.push({ path: "patient.fullName", expected: "string (non-empty)", value: objObj_patientObj.fullName });
            }
          }
          // birthDate (required)
          if (objObj_patientObj.birthDate === undefined) {
            errors.push({ path: "patient.birthDate", expected: "string", value: undefined });
          } else {
            if (typeof objObj_patientObj.birthDate !== "string") {
              errors.push({ path: "patient.birthDate", expected: "string", value: objObj_patientObj.birthDate });
            }
          }
        }
      }
    }
  
  if (errors.length === 0 && isAccountSetupIdentityResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "AccountSetupIdentityResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * AccountSetupIdentityResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertAccountSetupIdentityResponseDto(input: unknown): AccountSetupIdentityResponseDto {
  const validation = validateAccountSetupIdentityResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertAccountSetupIdentityResponseDto] :: 검증 실패: AccountSetupIdentityResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
